import type { ClientsRepository, SearchParams } from '@@/types/ClientsRepository';
import type { Client, Subscription } from '@@/types/Clients';
import type { ASSearchParams, JSONApiClient } from '../serializers/clients.js';
import type { JSONApiResponse } from '../serializers/jsonapi.js';

import { toAuthServiceSearchParams, subscriptionsPayload, clientPayload } from '../serializers/clients';
import { deserializeJSONApi, JSONApiHeaders } from '../serializers/jsonapi.js';
import { useAuthServiceApi } from '~/composables/useAuthServiceApi.js';

export default function Clients(): ClientsRepository {
  return {
    async search(params: SearchParams) {
      const reqParams: ASSearchParams = toAuthServiceSearchParams(params);

      const response = await useAuthServiceApi<JSONApiResponse<JSONApiClient[]>>('/clients', {
        method: 'GET',
        params: reqParams,
      });

      const data = await deserializeJSONApi<JSONApiResponse<JSONApiClient[]>, Client[]>(response);

      return { data, total: response.meta['record-count'] };
    },
    async find(id: number): Promise<Client> {
      const response = await useAuthServiceApi<JSONApiResponse<JSONApiClient>>(`/clients/${id}`, {
        method: 'GET',
        params: { include: 'subscriptions' },
      });
      if (typeof response.data.attributes.notes === 'string') {
        try {
          response.data.attributes.notes = JSON.parse(response.data.attributes.notes);
        } catch (_e) {
          response.data.attributes.notes = {};
        }
      }
      const data = await deserializeJSONApi<JSONApiResponse<JSONApiClient>, Client>(response);
      return data;
    },
    async saveClient(client: Partial<Client>) {
      const method = client.id ? 'PATCH' : 'POST';
      const url = client.id ? `/clients/${client.id}` : '/clients';
      const resp = await useAuthServiceApi<JSONApiResponse<JSONApiClient>>(url, {
        method,
        body: clientPayload(client),
        headers: JSONApiHeaders,
      });
      return deserializeJSONApi<JSONApiResponse<JSONApiClient>, Client>(resp);
    },
    //TODO: move this into subscriptions repository if needed
    async createSubscription(clientId: number | string, oauthApplicationId: number) {
      return useAuthServiceApi('/subscriptions', {
        method: 'POST',
        body: subscriptionsPayload(clientId, { oauthApplicationId }),
        headers: JSONApiHeaders,
      });
    },
    async updateSubscription(clientId: number | string, subscription: Partial<Subscription>) {
      return useAuthServiceApi(`/subscriptions/${subscription.id}`, {
        method: 'PATCH',
        body: subscriptionsPayload(clientId, subscription),
        headers: JSONApiHeaders,
      });
    },
    async deleteSubscription(subscriptionId: number) {
      return useAuthServiceApi(`/subscriptions/${subscriptionId}`, { method: 'DELETE' });
    },
  };
}
